import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import DashboardLayout from '../dashboardLayout';
import {
	getQrEventsApi,
	createQrEventApi,
	updateQrEventApi,
	deleteQrEventApi,
} from '../../../api/adminApi';
import { PulseLoader } from 'react-spinners';
import { errorToast } from '../../../helpers/projectHelper';
import './qrEventManagement.css';

const QrEventManagement = () => {
	const dispatch = useDispatch();
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [editingEvent, setEditingEvent] = useState(null);
	const [formLoading, setFormLoading] = useState(false);

	// Form states
	const [formData, setFormData] = useState({
		name: '',
		displayName: '',
		description: '',
		isActive: true,
		allowMultipleScans: false,
		maxScansPerUser: 1,
		startDate: '',
		endDate: '',
		timeSlots: [],
		requiresSeatAssignment: false,
		requiresCheckedIn: false,
		buttonColor: '#007bff',
		buttonIcon: 'qr-code',
	});

	const [timeSlot, setTimeSlot] = useState({
		name: '',
		startTime: '',
		endTime: '',
		isActive: true,
	});

	const loadEvents = useCallback(() => {
		dispatch(
			getQrEventsApi(
				setEvents,
				() => {}, // totalCount not needed
				setLoading,
			),
		);
	}, [dispatch]);

	useEffect(() => {
		loadEvents();
	}, [loadEvents]);

	// Handle body class for modal
	useEffect(() => {
		if (showModal) {
			document.body.classList.add('modal-open');
		} else {
			document.body.classList.remove('modal-open');
		}

		// Cleanup on unmount
		return () => {
			document.body.classList.remove('modal-open');
		};
	}, [showModal]);

	const resetForm = () => {
		setFormData({
			name: '',
			displayName: '',
			description: '',
			isActive: true,
			allowMultipleScans: false,
			maxScansPerUser: 1,
			startDate: '',
			endDate: '',
			timeSlots: [],
			requiresSeatAssignment: false,
			requiresCheckedIn: false,
			buttonColor: '#007bff',
			buttonIcon: 'qr-code',
		});
		setEditingEvent(null);
	};

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;

		if (name.includes('.')) {
			const [parent, child] = name.split('.');
			setFormData((prev) => ({
				...prev,
				[parent]: {
					...prev[parent],
					[child]: type === 'checkbox' ? checked : value,
				},
			}));
		} else {
			setFormData((prev) => ({
				...prev,
				[name]:
					type === 'checkbox'
						? checked
						: type === 'number'
						? parseInt(value) || 0
						: value,
			}));
		}
	};

	const addTimeSlot = () => {
		if (!timeSlot.name || !timeSlot.startTime || !timeSlot.endTime) {
			errorToast('Please fill all time slot fields');
			return;
		}

		setFormData((prev) => ({
			...prev,
			timeSlots: [...prev.timeSlots, { ...timeSlot }],
		}));

		setTimeSlot({
			name: '',
			startTime: '',
			endTime: '',
			isActive: true,
		});
	};

	const removeTimeSlot = (index) => {
		setFormData((prev) => ({
			...prev,
			timeSlots: prev.timeSlots.filter((_, i) => i !== index),
		}));
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if (!formData.name || !formData.displayName) {
			errorToast('Name and Display Name are required');
			return;
		}

		const apiCall = editingEvent ? updateQrEventApi : createQrEventApi;
		const params = editingEvent
			? [editingEvent._id, formData, setFormLoading, handleFormSuccess]
			: [formData, setFormLoading, handleFormSuccess];

		dispatch(apiCall(...params));
	};

	const handleFormSuccess = () => {
		setShowModal(false);
		resetForm();
		loadEvents();
	};

	const closeModal = () => {
		setShowModal(false);
		document.body.classList.remove('modal-open');
	};

	const handleEdit = (event) => {
		setEditingEvent(event);
		setFormData({
			...event,
			startDate: event.startDate
				? new Date(event.startDate).toISOString().slice(0, 16)
				: '',
			endDate: event.endDate
				? new Date(event.endDate).toISOString().slice(0, 16)
				: '',
		});
		setShowModal(true);
	};

	const handleDelete = (eventId) => {
		if (window.confirm('Are you sure you want to delete this event?')) {
			dispatch(deleteQrEventApi(eventId, setLoading, loadEvents));
		}
	};

	const iconOptions = [
		'qr-code',
		'check-circle',
		'user-check',
		'calendar-check',
		'utensils',
		'coffee',
		'pray',
		'book-open',
		'users',
		'heart',
	];

	return (
		<DashboardLayout>
			<div className='container mt-4 qr-event-management'>
				<div className='qr-event-header'>
					<h3>QR Event Management</h3>
					<button
						className='btn btn-create-event'
						onClick={() => {
							resetForm();
							setShowModal(true);
						}}
					>
						<i className='fas fa-plus me-2'></i>
						Create New Event
					</button>
				</div>

				{loading ? (
					<div className='loading-container'>
						<PulseLoader color='#0e5472' />
						<p className='mt-3 mb-0'>Loading events...</p>
					</div>
				) : events.length === 0 ? (
					<div className='empty-state'>
						<i className='fas fa-calendar-times'></i>
						<h5>No QR Events Found</h5>
						<p>Get started by creating your first QR event.</p>
					</div>
				) : (
					<>
						{/* Events Table */}
						<div className='table-responsive fade-in'>
							<table className='table'>
								<thead>
									<tr>
										<th>Event Details</th>
										<th>Status & Scan Type</th>
										<th>Scan Settings</th>
										<th>Time Slots</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									{events.map((event) => (
										<tr key={event._id}>
											<td>
												<div className='event-icon-preview'>
													<i
														className={`fas fa-${event.buttonIcon}`}
														style={{ color: event.buttonColor }}
													></i>
													<div>
														<strong>{event.displayName}</strong>
														<br />
														<small className='text-muted'>{event.name}</small>
													</div>
												</div>
											</td>
											<td>
												<div className='status-badges'>
													<span
														className={`badge ${
															event.allowMultipleScans
																? 'bg-warning'
																: 'bg-success'
														}`}
													>
														{event.allowMultipleScans
															? `Multiple Scans (${event.maxScansPerUser})`
															: 'Single Scan'}
													</span>
													<span
														className={`badge ${
															event.isActive ? 'bg-success' : 'bg-secondary'
														}`}
													>
														{event.isActive ? 'Active' : 'Inactive'}
													</span>
												</div>
											</td>
											<td>
												<div>
													<strong>
														{event.allowMultipleScans
															? `Max ${event.maxScansPerUser}`
															: 'Single Scan'}
													</strong>
													{event.requiresSeatAssignment && (
														<>
															<br />
															<small className='text-info'>
																<i className='fas fa-chair me-1'></i>
																Seat Required
															</small>
														</>
													)}
												</div>
											</td>
											<td>
												<div className='text-center'>
													<span className='badge bg-info'>
														{event.timeSlots?.length || 0} slots
													</span>
												</div>
											</td>
											<td>
												<div className='d-flex gap-2 justify-content-center'>
													<button
														className='btn btn-sm btn-outline-primary'
														onClick={() => handleEdit(event)}
														title='Edit Event'
													>
														<i className='fas fa-edit'></i>
													</button>
													<button
														className='btn btn-sm btn-outline-danger'
														onClick={() => handleDelete(event._id)}
														title='Delete Event'
													>
														<i className='fas fa-trash'></i>
													</button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* Pagination could be added here if needed */}
					</>
				)}

				{/* Modal */}
				{showModal &&
					createPortal(
						<div
							className='modal fade show qr-event-modal'
							style={{ display: 'block' }}
						>
							<div className='modal-dialog modal-lg'>
								<div className='modal-content'>
									<form onSubmit={handleSubmit}>
										<div className='modal-header'>
											<h5 className='modal-title'>
												{editingEvent ? 'Edit' : 'Create'} QR Event
											</h5>
											<button
												type='button'
												className='btn-close'
												onClick={closeModal}
											></button>
										</div>
										<div className='modal-body'>
											{/* Basic Info */}
											<div className='row mb-3'>
												<div className='col-md-6'>
													<label className='form-label'>Name (Internal)*</label>
													<input
														type='text'
														className='form-control'
														name='name'
														value={formData.name}
														onChange={handleInputChange}
														required
													/>
												</div>
												<div className='col-md-6'>
													<label className='form-label'>Display Name*</label>
													<input
														type='text'
														className='form-control'
														name='displayName'
														value={formData.displayName}
														onChange={handleInputChange}
														required
													/>
												</div>
											</div>

											<div className='mb-3'>
												<label className='form-label'>Description</label>
												<textarea
													className='form-control'
													name='description'
													value={formData.description}
													onChange={handleInputChange}
													rows={3}
												/>
											</div>

											{/* UI Settings */}
											<div className='row mb-3'>
												<div className='col-md-6'>
													<label className='form-label'>Button Icon</label>
													<select
														className='form-select'
														name='buttonIcon'
														value={formData.buttonIcon}
														onChange={handleInputChange}
													>
														{iconOptions.map((icon) => (
															<option key={icon} value={icon}>
																{icon}
															</option>
														))}
													</select>
												</div>
												<div className='col-md-6'>
													<label className='form-label'>Button Color</label>
													<input
														type='color'
														className='form-control form-control-color'
														name='buttonColor'
														value={formData.buttonColor}
														onChange={handleInputChange}
													/>
												</div>
											</div>

											{/* Checkboxes */}
											<div className='row mb-3'>
												<div className='col-md-3'>
													<div className='form-check'>
														<input
															className='form-check-input'
															type='checkbox'
															name='isActive'
															checked={formData.isActive}
															onChange={handleInputChange}
														/>
														<label className='form-check-label'>Active</label>
													</div>
												</div>
												<div className='col-md-3'>
													<div className='form-check'>
														<input
															className='form-check-input'
															type='checkbox'
															name='allowMultipleScans'
															checked={formData.allowMultipleScans}
															onChange={handleInputChange}
														/>
														<label className='form-check-label'>
															Multiple Scans
														</label>
													</div>
												</div>
												<div className='col-md-3'>
													<div className='form-check'>
														<input
															className='form-check-input'
															type='checkbox'
															name='requiresSeatAssignment'
															checked={formData.requiresSeatAssignment}
															onChange={handleInputChange}
														/>
														<label className='form-check-label'>
															Require Seat
														</label>
													</div>
												</div>
												<div className='col-md-3'>
													<div className='form-check'>
														<input
															className='form-check-input'
															type='checkbox'
															name='requiresCheckedIn'
															checked={formData.requiresCheckedIn}
															onChange={handleInputChange}
														/>
														<label className='form-check-label'>
															Require Check-in
														</label>
													</div>
												</div>
											</div>

											{/* Max Scans */}
											{formData.allowMultipleScans && (
												<div className='row mb-3'>
													<div className='col-md-6'>
														<label className='form-label'>
															Max Scans Per User
														</label>
														<input
															type='number'
															className='form-control'
															name='maxScansPerUser'
															value={formData.maxScansPerUser}
															onChange={handleInputChange}
															min='1'
														/>
													</div>
												</div>
											)}

											{/* Date Range */}
											<div className='row mb-3'>
												<div className='col-md-6'>
													<label className='form-label'>Start Date</label>
													<input
														type='datetime-local'
														className='form-control'
														name='startDate'
														value={formData.startDate}
														onChange={handleInputChange}
													/>
												</div>
												<div className='col-md-6'>
													<label className='form-label'>End Date</label>
													<input
														type='datetime-local'
														className='form-control'
														name='endDate'
														value={formData.endDate}
														onChange={handleInputChange}
													/>
												</div>
											</div>

											<div className='mb-3'>
												<label className='form-label'>Time Slots</label>
												<div className='time-slots-container'>
													<div className='row mb-3'>
														<div className='col-md-4'>
															<input
																type='text'
																className='form-control'
																placeholder='Slot name (e.g. Morning Session)'
																value={timeSlot.name}
																onChange={(e) =>
																	setTimeSlot((prev) => ({
																		...prev,
																		name: e.target.value,
																	}))
																}
															/>
														</div>
														<div className='col-md-3'>
															<input
																type='time'
																className='form-control'
																placeholder='Start time'
																value={timeSlot.startTime}
																onChange={(e) =>
																	setTimeSlot((prev) => ({
																		...prev,
																		startTime: e.target.value,
																	}))
																}
															/>
														</div>
														<div className='col-md-3'>
															<input
																type='time'
																className='form-control'
																placeholder='End time'
																value={timeSlot.endTime}
																onChange={(e) =>
																	setTimeSlot((prev) => ({
																		...prev,
																		endTime: e.target.value,
																	}))
																}
															/>
														</div>
														<div className='col-md-2'>
															<button
																type='button'
																className='btn btn-success w-100'
																onClick={addTimeSlot}
																title='Add Time Slot'
															>
																<i className='fas fa-plus'></i>
															</button>
														</div>
													</div>

													{formData.timeSlots.length > 0 && (
														<div className='mt-3'>
															<h6 className='text-muted mb-2'>
																Added Time Slots:
															</h6>
															{formData.timeSlots.map((slot, index) => (
																<div key={index} className='time-slot-item'>
																	<div>
																		<strong>{slot.name}</strong>
																		<br />
																		<small className='text-muted'>
																			{slot.startTime} - {slot.endTime}
																		</small>
																	</div>
																	<button
																		type='button'
																		className='btn btn-sm btn-danger'
																		onClick={() => removeTimeSlot(index)}
																		title='Remove Time Slot'
																	>
																		<i className='fas fa-times'></i>
																	</button>
																</div>
															))}
														</div>
													)}

													{formData.timeSlots.length === 0 && (
														<div className='text-center text-muted py-3'>
															<i className='fas fa-clock fa-2x mb-2'></i>
															<p>
																No time slots added yet. Add your first time
																slot above.
															</p>
														</div>
													)}
												</div>
											</div>
										</div>
										<div className='modal-footer'>
											<button
												type='button'
												className='btn btn-secondary'
												onClick={closeModal}
											>
												Cancel
											</button>
											<button
												type='submit'
												className='btn btn-primary'
												disabled={formLoading}
											>
												{formLoading ? (
													<>
														<PulseLoader
															size={6}
															color='#fff'
															className='me-2'
														/>
														Saving...
													</>
												) : (
													<>
														<i className='fas fa-save me-2'></i>
														{editingEvent ? 'Update' : 'Create'} Event
													</>
												)}
											</button>
										</div>
									</form>
								</div>
							</div>
							<div
								className='modal-backdrop fade show'
								onClick={closeModal}
							></div>
						</div>,
						document.body,
					)}
			</div>
		</DashboardLayout>
	);
};

export default QrEventManagement;
